import { makeAutoObservable, observableRef } from 'mobx';
import memoize from 'lodash/memoize';
import { isNumber } from '@cutwise/utils';

import { ConvertedDictionaries, Dictionaries } from '@/common/types/dictionaries';
import {
  BaseDictItem,
  BaseDictItemWithChildren,
  FancyGradeDictItem,
  ServiceDictItem,
} from '@/common/types/dictionaryItem';
import { FancyColorSubgradeType } from '@/product/constants/FancyColorSubgradeType';
import { GemTypeId } from '@/product/constants/GemTypeId';
import { MAIN_SHAPES_IDS, SHAPE_OTHER } from '@/product/entity/CutShape';
import { RenderMode } from '@/tasks/constants/renderModes';
import { TaskStatus } from '@/tasks/constants/TaskStatus';

import DictionaryConverter from '../services/DictionaryConverter';

export default class DictionaryRepository {
  dictionaries!: ConvertedDictionaries;

  static modifyFancyColors(colorHue: BaseDictItemWithChildren[]) {
    const result: BaseDictItem[] = [];

    colorHue.forEach((item) => {
      result.push(item);

      if (item.children && item.children.length) {
        item.children.forEach((child) => {
          result.push(child);
        });
      }
    });

    return result;
  }

  // https://octonus-teams.com/jira/browse/CUT-7019
  static modifyShapes(shapes: BaseDictItem[]) {
    const shapeOther = shapes.find((s) => s.id === SHAPE_OTHER);

    if (shapeOther) {
      // add all rare cutShapes to 'Other' as children
      (shapeOther as BaseDictItemWithChildren).children = shapes.filter(
        (s) => MAIN_SHAPES_IDS.includes(s.id) === false,
      );
    }

    return shapes.filter((s) => MAIN_SHAPES_IDS.includes(s.id) === true);
  }

  initDictionaries(dictionariesData: Dictionaries) {
    dictionariesData.colorHueAll = DictionaryRepository.modifyFancyColors(dictionariesData.colorHue);
    dictionariesData.cutShape = DictionaryRepository.modifyShapes(dictionariesData.cutShape);

    this.dictionaries = DictionaryConverter.convert(dictionariesData);
  }

  constructor() {
    makeAutoObservable(this, {
      dictionaries: observableRef,
    });

    this.getColorById = memoize(this.getColorById);
    this.getClarityById = memoize(this.getClarityById);
    this.getCutQualityById = memoize(this.getCutQualityById);
    this.getSymmetryById = memoize(this.getSymmetryById);
    this.getPolishById = memoize(this.getPolishById);
    this.getFluorescenceStrengthById = memoize(this.getFluorescenceStrengthById);
    this.getFancyGradeById = memoize(this.getFancyGradeById);
    this.getFancyColorDescHueById = memoize(this.getFancyColorDescHueById);
    this.getColorModifierById = memoize(this.getColorModifierById);
    this.getGirdleThicknessGradeById = memoize(this.getGirdleThicknessGradeById);
    this.getFluorescenceColorById = memoize(this.getFluorescenceColorById);
    this.getLaboratoryById = memoize(this.getLaboratoryById);
    this.getCuletSizeGradeById = memoize(this.getCuletSizeGradeById);
    this.getColorByTitle = memoize(this.getColorByTitle);
    this.getCutShapeById = memoize(this.getCutShapeById);
    this.getGemTypeById = memoize(this.getGemTypeById);
    this.getTransactionTypeById = memoize(this.getTransactionTypeById);
  }

  getFancyGradePercentGrade(id: number) {
    if (!this.dictionaries) {
      throw new Error('Dictionary not found');
    }

    return (
      (this.dictionaries.fancyGrade.length - this.dictionaries.fancyGrade.findIndex((i) => i.id === id)) *
      (100 / this.dictionaries.fancyGrade.length)
    );
  }

  getColorById(id: number) {
    return this.findItemInDictionaryById('color', id);
  }

  getClarityById(id: number) {
    return this.findItemInDictionaryById('clarity', id);
  }

  getCutQualityById(id: number) {
    return this.findItemInDictionaryById('cutQuality', id);
  }

  getSymmetryById(id: number) {
    return this.findItemInDictionaryById('symmetry', id);
  }

  getPolishById(id: number) {
    return this.findItemInDictionaryById('polish', id);
  }

  getFluorescenceStrengthById(id: number) {
    return this.findItemInDictionaryById('fluorIntensity', id);
  }

  getFancyGradeById(id: number) {
    return this.findItemInDictionaryById<FancyGradeDictItem>('fancyGrade', id);
  }

  getFancyColorDescHueById(id: number) {
    return this.findItemInDictionaryById('colorHueAll', id);
  }

  getColorModifierById(id: number) {
    return this.findItemInDictionaryById('colorModifier', id);
  }

  getGirdleThicknessGradeById(id: number) {
    return this.findItemInDictionaryById('girdleThicknessGrade', id);
  }

  getFluorescenceColorById(id: number) {
    return this.findItemInDictionaryById('fluorColor', id);
  }

  getLaboratoryById(id: number) {
    return this.findItemInDictionaryById('laboratory', id);
  }

  getCuletSizeGradeById(id: number) {
    return this.findItemInDictionaryById('culet', id);
  }

  getCutShapeById(id: number) {
    return this.findItemInDictionaryById('cutShape', id);
  }

  getCutShapeTitleById(id: Nullable<number>) {
    return (isNumber(id) && this.getCutShapeById(id)?.title) || 'Other';
  }

  getColorByTitle(title: string) {
    return this.findItemInDictionaryByTitle('color', title);
  }

  getIndexByColorTitle(title: string) {
    return this.findIndexInDictionaryByTitle('color', title);
  }

  getGemTypeById(id: GemTypeId) {
    return this.findItemInDictionaryById('gemTypes', id);
  }

  getMilkyById(id: number) {
    return this.findItemInDictionaryById('milky', id);
  }

  getTreatmentById(id: string) {
    return this.findItemInDictionaryById('treatment', id);
  }

  getServiceById(id: number) {
    return this.findItemInDictionaryById('services', id) as ServiceDictItem | undefined;
  }

  getTaskTypeById(id: number) {
    return this.findItemInDictionaryById('taskTypes', id);
  }

  getTaskStatusById(id: TaskStatus) {
    return this.findItemInDictionaryById('taskStatuses', id);
  }

  getRenderModeById(id: RenderMode) {
    return this.findItemInDictionaryById('renderingMode', id);
  }

  getTransactionTypeById(id: number) {
    return this.findItemInDictionaryById('transactionTypes', id);
  }

  getSetupPresetById(id: number) {
    return this.findItemInDictionaryById('setupPresets', id);
  }

  getSubgradeTitle(type: FancyColorSubgradeType, id?: Nullable<number>): Nullable<string> {
    if (!id) {
      return null;
    }

    return this.findItemInDictionaryById(type, id)?.title ?? null;
  }

  findItemInDictionaryById<T extends BaseDictItem>(
    name: keyof ConvertedDictionaries,
    id: StringOrNumber | RenderMode | GemTypeId,
  ): Nullable<T> {
    return (this.dictionaries?.[name] as T[])?.find((i) => i.id === id) ?? null;
  }

  findItemInDictionaryByTitle(name: keyof ConvertedDictionaries, title: string) {
    return (
      (this.dictionaries?.[name] as BaseDictItem[])?.find((i) => i.title?.toUpperCase() === title.toUpperCase()) ?? null
    );
  }

  findIndexInDictionaryByTitle(name: keyof ConvertedDictionaries, title: string) {
    return (
      (this.dictionaries?.[name] as BaseDictItem[])?.findIndex((i) => i.title?.toUpperCase() === title.toUpperCase()) ??
      null
    );
  }
}

export const dictionaryRepository = new DictionaryRepository();
